import React, { useState, useRef } from "react";
import { Phone, Loader2, Mail, ArrowRight, MessageCircle, Clock, CheckCircle2 } from "lucide-react";
import { useInView } from "react-intersection-observer";
import ReCAPTCHA from "react-google-recaptcha";
import { sendContactForm } from "../services/contactService";
import Section from "./Section";

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
  recaptcha?: string;
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "お名前を入力してください";
    if (!formData.email.trim()) {
      newErrors.email = "メールアドレスを入力してください";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = "有効なメールアドレスを入力してください";
    }
    if (!formData.message.trim()) newErrors.message = "お問い合わせ内容を入力してください";
    if (!recaptchaToken) newErrors.recaptcha = "reCAPTCHA認証を完了してください";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitStatus("idle");
    try {
      if (!recaptchaToken) throw new Error("reCAPTCHAトークンが無効です");
      await sendContactForm(formData, recaptchaToken);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "" });
      setRecaptchaToken(null);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    } catch (error) {
      console.error("Contact form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    if (token && errors.recaptcha) {
      setErrors((prev) => ({ ...prev, recaptcha: undefined }));
    }
  };

  const { ref: animRef, inView: animInView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const promises = [
    { icon: Clock, text: '30分のオンライン相談（無料）' },
    { icon: MessageCircle, text: '「こんなこと聞いていいの？」レベルでOK' },
    { icon: CheckCircle2, text: '相談後、最短翌日に計画書をお届け' },
  ];

  return (
    <Section id="contact" className="relative bg-[#1B2A4A] overflow-hidden">
      {/* 背景装飾 */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_20%_50%,rgba(74,111,165,0.15),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_600px_at_80%_80%,rgba(184,134,11,0.08),transparent)]" />
      </div>

      <div className="relative py-20 lg:py-28">
        <div ref={animRef} className={`mx-auto max-w-6xl px-5 lg:px-6 transition-all duration-1000 ${animInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* ヘッダー */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/40 bg-[#B8860B]/10 px-4 py-1.5 mb-6">
              <span className="text-sm font-semibold text-[#B8860B]">まずはご相談ください</span>
            </div>
            <h2 className="text-3xl font-bold lg:text-[2.8rem] leading-tight text-[#F5F3EF]">
              <span className="block mb-2">無料相談</span>
              <span className="text-[#B8860B]">受付中</span>
              <span className="text-xl lg:text-2xl text-[#F5F3EF]/60 ml-2">（30分・オンライン）</span>
            </h2>
            {/* 緊急性・希少性 */}
            <div className="inline-flex items-center gap-2 rounded-full border border-[#B8860B]/40 bg-[#B8860B]/15 px-5 py-2 mt-5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B8860B] opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#B8860B]" />
              </span>
              <span className="text-sm font-semibold text-[#B8860B]">
                代表が1人で対応のため、月の相談枠に限りがあります
              </span>
            </div>
            <p className="text-[#F5F3EF]/60 mt-4 max-w-lg mx-auto">
              「こんなシステムが欲しい」「今の業務をラクにしたい」など、ざっくりで構いません。
            </p>
          </div>

          {/* 3つの約束 */}
          <div className="grid sm:grid-cols-3 gap-4 mb-12 max-w-3xl mx-auto">
            {promises.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="flex items-center gap-3 rounded-xl border border-[#F5F3EF]/10 bg-white/5 px-4 py-3">
                  <Icon className="h-5 w-5 text-[#B8860B] shrink-0" />
                  <span className="text-sm text-[#F5F3EF]/90">{item.text}</span>
                </div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-5 gap-8 max-w-5xl mx-auto">
            {/* フォーム */}
            <div className="lg:col-span-3 rounded-2xl border border-[#E8E6E1] bg-white p-6 lg:p-8" style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' }}>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-1.5">
                    お名前 <span className="text-[#9B4D3A]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="山田 太郎"
                    className={`w-full rounded-xl border ${errors.name ? "border-[#9B4D3A]" : "border-[#E8E6E1]"} bg-[#FAFAF8] px-4 py-3 text-[#2D3748] placeholder:text-[#D5D2CC] focus:ring-2 focus:ring-[#B8860B]/40 focus:border-[#B8860B] focus:bg-white transition-colors`}
                  />
                  {errors.name && <p className="mt-1 text-sm text-[#9B4D3A]">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-1.5">
                    メールアドレス <span className="text-[#9B4D3A]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@company.com"
                    className={`w-full rounded-xl border ${errors.email ? "border-[#9B4D3A]" : "border-[#E8E6E1]"} bg-[#FAFAF8] px-4 py-3 text-[#2D3748] placeholder:text-[#D5D2CC] focus:ring-2 focus:ring-[#B8860B]/40 focus:border-[#B8860B] focus:bg-white transition-colors`}
                  />
                  {errors.email && <p className="mt-1 text-sm text-[#9B4D3A]">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2D3748] mb-1.5">
                    ご相談内容 <span className="text-[#9B4D3A]">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="例：顧客データの管理をシステム化したい、ホームページを作りたい"
                    className={`w-full rounded-xl border ${errors.message ? "border-[#9B4D3A]" : "border-[#E8E6E1]"} bg-[#FAFAF8] px-4 py-3 text-[#2D3748] placeholder:text-[#D5D2CC] focus:ring-2 focus:ring-[#B8860B]/40 focus:border-[#B8860B] focus:bg-white transition-colors`}
                  />
                  {errors.message && <p className="mt-1 text-sm text-[#9B4D3A]">{errors.message}</p>}
                </div>

                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LdXxNwqAAAAAOMZ6SphYqR7JiHofyIqZvVfu4d3"}
                    onChange={handleRecaptchaChange}
                    onExpired={() => setRecaptchaToken(null)}
                    onError={() => setRecaptchaToken(null)}
                  />
                </div>
                {errors.recaptcha && <p className="text-center text-sm text-[#9B4D3A]">{errors.recaptcha}</p>}

                {submitStatus === "success" && (
                  <div className="rounded-xl border border-[#2D8659]/30 bg-[#2D8659]/10 px-5 py-4 text-[#2D8659]">
                    お問い合わせを受け付けました。担当者より順次ご連絡させていただきます。
                  </div>
                )}
                {submitStatus === "error" && (
                  <div className="rounded-xl border border-[#9B4D3A]/30 bg-[#9B4D3A]/10 px-5 py-4 text-[#9B4D3A]">
                    送信に失敗しました。お手数ですが、時間をおいて再度お試しください。
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-8 py-4 text-lg font-bold text-white transition-all ${
                    isSubmitting
                      ? "bg-[#D5D2CC] cursor-not-allowed"
                      : "bg-[#B8860B] hover:bg-[#A0750A] shadow-xl shadow-[#B8860B]/30 hover:shadow-2xl hover:shadow-[#B8860B]/40"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      送信中...
                    </>
                  ) : (
                    <>
                      無料相談を申し込む
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* サイドカード */}
            <div className="lg:col-span-2 space-y-6 self-start">
              {/* 電話 */}
              <div className="rounded-2xl border border-[#F5F3EF]/10 bg-white/5 backdrop-blur-sm p-6">
                <div className="text-xs font-bold tracking-widest text-[#B8860B] mb-4">お急ぎの方へ</div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#B8860B]/20">
                    <Phone className="h-6 w-6 text-[#B8860B]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#F5F3EF]">082-209-0181</div>
                    <div className="text-xs text-[#F5F3EF]/50">平日 10:00〜17:00</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#B8860B]/20">
                    <Mail className="h-5 w-5 text-[#B8860B]" />
                  </div>
                  <div>
                    <div className="text-sm text-[#F5F3EF]/80">
                      <span className="select-all">info</span>
                      <span className="text-[#F5F3EF]/30 mx-1">[at]</span>
                      <span className="select-all">senrigan</span>
                      <span className="text-[#F5F3EF]/30 mx-1">[dot]</span>
                      <span className="select-all">systems</span>
                    </div>
                    <div className="text-xs text-[#F5F3EF]/40 mt-0.5">※ [at]を@に、[dot]を.に置き換え</div>
                  </div>
                </div>
              </div>

              {/* よくあるご相談 */}
              <div className="rounded-2xl border border-[#F5F3EF]/10 bg-white/5 backdrop-blur-sm p-6">
                <div className="text-xs font-bold tracking-widest text-[#B8860B] mb-4">よくあるご相談内容</div>
                <ul className="space-y-3 text-sm text-[#F5F3EF]/80">
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#B8860B] mt-0.5 shrink-0">—</span>
                    <span>集客用のLP・ホームページを作りたい</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#B8860B] mt-0.5 shrink-0">—</span>
                    <span>顧客管理や在庫管理をシステム化したい</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#B8860B] mt-0.5 shrink-0">—</span>
                    <span>手作業の集計や報告書を自動化したい</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <span className="text-[#B8860B] mt-0.5 shrink-0">—</span>
                    <span>スマホから使える業務システムが欲しい</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Contact;
